import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Checkbox,
  HStack,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import * as React from 'react';

export type PolicyChangePreviewMember = {
  userId: string;
  displayName?: string | null;
  userGroupRole?: string | null;
  subgroupNames?: string[] | null;
};

const memberSubgroupNames = (member: PolicyChangePreviewMember): string[] =>
  (member.subgroupNames || []).filter(Boolean);

export const PolicyChangePreviewModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (excludedUserIds: string[]) => Promise<void> | void;
  suspendCount: number;
  restoreCount: number;
  membersToSuspend: PolicyChangePreviewMember[];
  membersToRestore?: PolicyChangePreviewMember[];
  isConfirmLoading?: boolean;
}> = ({
  isOpen,
  onClose,
  onConfirm,
  suspendCount: _suspendCount,
  restoreCount: _restoreCount,
  membersToSuspend,
  membersToRestore = [],
  isConfirmLoading,
}) => {
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const [search, setSearch] = React.useState('');
  const [excludedUserIds, setExcludedUserIds] = React.useState<Set<string>>(
    new Set(),
  );

  React.useEffect(() => {
    if (isOpen) {
      setExcludedUserIds(new Set());
      setSearch('');
    }
  }, [isOpen]);

  const filteredMembers = membersToSuspend.filter((member) => {
    if (!search.trim()) return true;
    const query = search.trim().toLowerCase();
    const nameMatches = (member.displayName || '')
      .toLowerCase()
      .includes(query);
    const subgroupMatches = memberSubgroupNames(member).some((name) =>
      name.toLowerCase().includes(query),
    );
    return nameMatches || subgroupMatches;
  });

  const effectiveSuspendCount = Math.max(
    membersToSuspend.length - excludedUserIds.size,
    0,
  );
  const hasRemovals = membersToSuspend.length > 0;
  const hasRestores = membersToRestore.length > 0;
  const memberWord = effectiveSuspendCount === 1 ? 'member' : 'members';
  const restoreWord = membersToRestore.length === 1 ? 'member' : 'members';
  const header = hasRemovals
    ? 'Changing these settings may suspend members'
    : hasRestores
    ? 'Changing these settings may restore members'
    : 'Confirm setting change';

  return (
    <AlertDialog
      leastDestructiveRef={cancelRef}
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      isCentered
    >
      <AlertDialogOverlay />
      <AlertDialogContent>
        <AlertDialogHeader borderBottom="1px solid" borderColor="gray.200">
          {header}
        </AlertDialogHeader>
        <AlertDialogBody>
          <VStack align="stretch" spacing="4">
            {effectiveSuspendCount > 0 ? (
              <>
                <Text>
                  This change will remove {effectiveSuspendCount} {memberWord}{' '}
                  from this group and may also remove members from its
                  subgroups.
                </Text>
                <Text>
                  Review the affected members and subgroups below. Uncheck
                  anyone you want to keep before applying the changes.
                </Text>
              </>
            ) : (
              <Text>
                No members will be removed from this group or its subgroups.
              </Text>
            )}
            {hasRestores && (
              <Text color="gray.600">
                {membersToRestore.length} {restoreWord} will be restored
                automatically because they now meet the policy.
              </Text>
            )}
            {hasRemovals && (
              <>
                <Input
                  placeholder="Search people or subgroups"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                <VStack
                  align="stretch"
                  spacing="2"
                  maxH="320px"
                  overflowY="auto"
                  border="1px solid"
                  borderColor="gray.100"
                  borderRadius="md"
                  p="3"
                >
                  {filteredMembers.map((member) => {
                    const willSuspend = !excludedUserIds.has(member.userId);
                    const subgroupNames = memberSubgroupNames(member);
                    return (
                      <Checkbox
                        key={member.userId}
                        isChecked={willSuspend}
                        alignItems="flex-start"
                        onChange={(event) => {
                          setExcludedUserIds((current) => {
                            const next = new Set(current);
                            if (event.target.checked) {
                              next.delete(member.userId);
                            } else {
                              next.add(member.userId);
                            }
                            return next;
                          });
                        }}
                      >
                        <HStack spacing="2" align="baseline" flexWrap="wrap">
                          <Text>{member.displayName || 'Unknown user'}</Text>
                          {subgroupNames.length > 0 && (
                            <Text color="gray.500" fontSize="sm">
                              {subgroupNames.join(', ')}
                            </Text>
                          )}
                        </HStack>
                      </Checkbox>
                    );
                  })}
                  {filteredMembers.length === 0 && (
                    <Text color="gray.500">No matching people</Text>
                  )}
                </VStack>
              </>
            )}
            {membersToRestore.length > 0 && (
              <VStack align="stretch" spacing="2">
                <Text fontWeight="semibold">Members who will be restored</Text>
                <VStack
                  align="stretch"
                  spacing="2"
                  maxH="240px"
                  overflowY="auto"
                  border="1px solid"
                  borderColor="gray.100"
                  borderRadius="md"
                  p="3"
                >
                  {membersToRestore.map((member) => {
                    const subgroupNames = memberSubgroupNames(member);
                    return (
                      <HStack
                        key={member.userId}
                        spacing="2"
                        align="baseline"
                        flexWrap="wrap"
                      >
                        <Text>{member.displayName || 'Unknown user'}</Text>
                        {subgroupNames.length > 0 && (
                          <Text color="gray.500" fontSize="sm">
                            {subgroupNames.join(', ')}
                          </Text>
                        )}
                      </HStack>
                    );
                  })}
                </VStack>
              </VStack>
            )}
          </VStack>
        </AlertDialogBody>
        <AlertDialogFooter>
          <Button
            ref={cancelRef}
            type="button"
            onClick={onClose}
            mr="3"
            variant="ghost"
          >
            Cancel
          </Button>
          <Button
            type="button"
            colorScheme="blue"
            isLoading={isConfirmLoading}
            onClick={() => onConfirm(Array.from(excludedUserIds))}
          >
            Confirm
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
